// apps/web/src/features/kronos/timer/timerMachine.ts

export const STORAGE_KEY = 'kronos-timer';

export interface TimerState {
  status: 'running' | 'paused' | 'stopped';
  projectId: string;
  designationId: string;
  description: string;      // notes added while timer is running
  startedAt: string;        // ISO timestamp
  pausedDuration: number;   // total ms spent paused
  pausedAt: string | null;  // ISO timestamp of current pause start
  autoStopAt: string | null; // ISO timestamp for auto-stop
}

// ─── Auto-stop presets ──────────────────────────────────────────────

export type AutoStopPreset =
  | 'lunch'     // 12:30 today
  | 'end-of-day' // 17:30 today
  | 'in-1h' | 'in-2h' | 'in-3h' | 'in-4h'
  | 'custom'    // handled by caller (passes ISO string directly)
  | 'none';

/**
 * Convert a preset to an absolute ISO timestamp, or null for 'none'.
 * 'custom' is not handled here — the caller passes the ISO string directly.
 */
export function computeAutoStopAt(preset: AutoStopPreset, now: number): string | null {
  if (preset === 'none') return null;
  if (preset === 'custom') return null; // caller handles this

  const relative: Record<string, number> = {
    'in-1h': 1, 'in-2h': 2, 'in-3h': 3, 'in-4h': 4,
  };
  if (preset in relative) {
    return new Date(now + relative[preset] * 3_600_000).toISOString();
  }

  // Absolute times: build today's date at the target hour
  const d = new Date(now);
  if (preset === 'lunch') {
    d.setHours(12, 30, 0, 0);
  } else if (preset === 'end-of-day') {
    d.setHours(17, 30, 0, 0);
  }
  // If the target time already passed today, it won't auto-stop (user picks it anyway)
  return d.toISOString();
}

// ─── Transitions ────────────────────────────────────────────────────

export function startTimer(params: {
  projectId: string;
  designationId: string;
  now: number;
  autoStopAt: string | null;
  description?: string;
}): TimerState {
  return {
    status: 'running',
    projectId: params.projectId,
    designationId: params.designationId,
    description: params.description ?? '',
    startedAt: new Date(params.now).toISOString(),
    pausedDuration: 0,
    pausedAt: null,
    autoStopAt: params.autoStopAt,
  };
}

export function pauseTimer(state: TimerState, now: number): TimerState {
  if (state.status !== 'running') {
    throw new Error(`Cannot pause timer in status: ${state.status}`);
  }
  return {
    ...state,
    status: 'paused',
    pausedAt: new Date(now).toISOString(),
  };
}

export function resumeTimer(state: TimerState, now: number): TimerState {
  if (state.status !== 'paused' || !state.pausedAt) {
    throw new Error(`Cannot resume timer in status: ${state.status}`);
  }
  const pauseMs = now - Date.parse(state.pausedAt);
  return {
    ...state,
    status: 'running',
    pausedAt: null,
    pausedDuration: state.pausedDuration + pauseMs,
  };
}

export function stopTimer(state: TimerState, now: number): TimerState {
  // If paused, accumulate the final pause duration before stopping
  let finalPauseDuration = state.pausedDuration;
  if (state.status === 'paused' && state.pausedAt) {
    finalPauseDuration += now - Date.parse(state.pausedAt);
  }
  return {
    ...state,
    status: 'stopped',
    pausedAt: null,
    pausedDuration: finalPauseDuration,
  };
}

// ─── Elapsed computation ────────────────────────────────────────────

/**
 * Compute elapsed active time in milliseconds.
 *
 * - Running: now - startedAt - pausedDuration
 * - Paused: pausedAt - startedAt - pausedDuration (frozen)
 * - Stopped: same as paused (frozen at stop time)
 *
 * If autoStopAt is set and `now` exceeds it, elapsed is capped at
 * the auto-stop time.
 */
export function computeElapsedMs(state: TimerState, now: number): number {
  const startedAt = Date.parse(state.startedAt);

  // Determine the effective "end" of the counting window
  let effectiveNow = now;
  if (state.autoStopAt && now > Date.parse(state.autoStopAt)) {
    effectiveNow = Date.parse(state.autoStopAt);
  }

  if (state.status === 'paused' && state.pausedAt) {
    // Frozen at pause time
    const pausedAt = Date.parse(state.pausedAt);
    return Math.max(0, pausedAt - startedAt - state.pausedDuration);
  }

  if (state.status === 'stopped') {
    // Frozen — use the effectiveNow (may be capped by autoStop)
    return Math.max(0, effectiveNow - startedAt - state.pausedDuration);
  }

  // Running
  return Math.max(0, effectiveNow - startedAt - state.pausedDuration);
}

// ─── Entry derivation ───────────────────────────────────────────────

/**
 * Derive the `(startTime, endTime, durationSeconds)` triple for a saved
 * TimeEntry from a timer state + its elapsed (active) milliseconds.
 *
 * `durationSeconds` is the active time (pauses excluded). `endTime`
 * advances past the paused span so the stored wall-clock window matches
 * reality: end = start + activeMs + pausedDuration. This is the same math
 * the stop-dialog save used inline; extracted so the recap's
 * continue-timer auto-save reuses it verbatim.
 */
export function computeTimerEntryTimes(
  state: TimerState,
  elapsedMs: number,
): { startTime: Date; endTime: Date; durationSeconds: number } {
  const startTime = new Date(state.startedAt);
  const durationSeconds = Math.floor(elapsedMs / 1000);
  const endTime = new Date(
    startTime.getTime() + durationSeconds * 1000 + state.pausedDuration,
  );
  return { startTime, endTime, durationSeconds };
}

// ─── Continue-timer decision ────────────────────────────────────────

/**
 * What pressing ▶ on a recap row should do, given the timer already
 * running.
 *
 * - `start`   no timer to preserve — just start the new one
 * - `keep`    ▶ hit the task already being timed: do nothing, keep counting
 * - `resume`  same, but the timer was paused
 * - `discard` switching task, but the current timer is too short to be
 *             worth a row — drop it
 * - `save`    switching task with real time on the clock — save, then start
 *
 * Before this existed the caller saved the running timer unconditionally,
 * so a second press on the SAME row wrote a 17-second entry that looked
 * like a duplicate of that row (prod, 30/07 and 18-21/09).
 */
export type ContinueTimerAction = 'start' | 'keep' | 'resume' | 'discard' | 'save';

/** Under a minute the UI renders "0:00" — the row is unreadable and unusable. */
// ponytail: fixed floor, make it a setting only if someone logs sub-minute work on purpose
export const MIN_TIMER_ENTRY_SECONDS = 60;

/**
 * The timer normally holds a raw designation id, but the edit popover can
 * leave an encoded `existing:<id>` in it — compare on the raw form.
 */
const rawDesignationId = (id: string): string =>
  id.startsWith('existing:') ? id.slice('existing:'.length) : id;

export function continueTimerAction(
  state: TimerState | null,
  elapsedMs: number,
  target: { projectId: string; designationId: string },
): ContinueTimerAction {
  if (!state || (state.status !== 'running' && state.status !== 'paused')) return 'start';

  const sameTask =
    state.projectId === target.projectId &&
    rawDesignationId(state.designationId) === rawDesignationId(target.designationId);
  if (sameTask) return state.status === 'paused' ? 'resume' : 'keep';

  return elapsedMs >= MIN_TIMER_ENTRY_SECONDS * 1000 ? 'save' : 'discard';
}

// ─── Stale detection ────────────────────────────────────────────────

function isSameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate();
}

/**
 * A timer is stale on mount if:
 * - It was running (no setInterval alive to tick it)
 * - It is stopped (auto-stop or manual stop, but not yet saved)
 * - It is from a previous day
 */
export function isStale(state: TimerState, now: number): boolean {
  if (state.status === 'stopped') return true;
  if (!isSameDay(Date.parse(state.startedAt), now)) return true;
  // If running on mount, it's stale — no interval is alive
  if (state.status === 'running') return true;
  // Paused from today — still stale on mount (user closed browser while paused)
  if (state.status === 'paused') return true;
  return false;
}

/**
 * For recovery: cap elapsed at end-of-day if timer is from a previous day
 * and no auto-stop was set. Returns the effective stop timestamp.
 */
export function computeRecoveryEndTime(state: TimerState, now: number): number {
  const startedAt = Date.parse(state.startedAt);

  // If auto-stop was set and time has passed, use that
  if (state.autoStopAt) {
    const autoStopTime = Date.parse(state.autoStopAt);
    if (now >= autoStopTime) return autoStopTime;
  }

  // If from a previous day, cap at end of that day (23:59:59)
  if (!isSameDay(startedAt, now)) {
    const endOfDay = new Date(startedAt);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay.getTime();
  }

  // Same day, no auto-stop — use now
  return now;
}

// ─── localStorage ───────────────────────────────────────────────────

export function writeTimer(state: TimerState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function readTimer(): TimerState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Basic shape validation
    if (!parsed || typeof parsed.status !== 'string' || !parsed.startedAt) return null;
    return parsed as TimerState;
  } catch {
    return null;
  }
}

export function clearTimer(): void {
  localStorage.removeItem(STORAGE_KEY);
}
